import { Theme, useMediaQuery, useTheme } from '@mui/material';
import Hidden from '@mui/material/Hidden';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';

import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { moreInfoOnClickHandler } from 'features/Connect/features/ExpressInterest/utils';
import { ExpressInterestButton } from '../Buttons/ExpressInterestButton';
import { SchoolInfoHighlightRow } from '../HighlightRows/HighlightRowSchoolInfo';
import { SchoolCardProps } from '../types';
import { SchoolTagsSection } from '../SchoolTags/SchoolTagsSection';
import { ConnectSchoolTagDisplayAtLocations } from 'features/Connect/utils/connectEnums';
import { QuickApplyButton } from '../Buttons/QuickApplyButton';
import { ConnectJobDiscoverySchoolCardDataTestIds } from 'data-testids/ConnectDataTestIds';
import { PrincipalComponent } from '../PrincipalComponent/PrincipalComponent';

export const BasicInfoSection: React.FC<SchoolCardProps> = ({ school, isInterestModal }) => {
  const { connectContextActions } = useConnectContext();
  const theme = useTheme();
  const hasPrincipal = !!school?.principal;
  const hasTags = !!school?.tags;
  const isInternalSchool = school?.is_internal_school;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          width: '100%',
          marginBottom: isInterestModal ? '0px' : isMobile ? '8px' : '16px',
        }}
      >
        {hasPrincipal && (
          <Hidden smDown>
            <PrincipalContainer
              href="#"
              onClick={() => moreInfoOnClickHandler(school, connectContextActions, '0')}
              sx={{
                alignItems: 'center',
                width: '100%',
                marginBottom: isInterestModal ? '0px' : '16px',
              }}
            >
              <PrincipalComponent principal={school.principal} showIconOrAvatar="avatar" />
            </PrincipalContainer>
          </Hidden>
        )}
        <Hidden smUp>
          <Stack direction={'column'} width={'100%'} spacing={1} gap={0.5}>
            {hasPrincipal && (
              <PrincipalComponent principal={school.principal} showIconOrAvatar="avatar" />
            )}
            {!isInterestModal && (
              <ButtonGroupStack>
                <ExpressInterestButton
                  school={school}
                  sx={expressInterestAndQuickApplyButtonStyles(theme)}
                />
                {isInternalSchool && (
                  <QuickApplyButton
                    sx={expressInterestAndQuickApplyButtonStyles(theme)}
                    isDistrictQuickApply={true}
                    school={school}
                    dataTestId={ConnectJobDiscoverySchoolCardDataTestIds.QUICK_APPLY_BUTTON}
                  />
                )}
              </ButtonGroupStack>
            )}
          </Stack>
        </Hidden>
      </Stack>
      <SchoolInfoHighlightRow school={school} isInterestModal={isInterestModal} />
      {hasTags && (
        <SchoolTagsSection
          school={school}
          display_at={ConnectSchoolTagDisplayAtLocations.SCHOOL_AND_MATCH_CARDS}
        />
      )}
    </Stack>
  );
};

const PrincipalContainer = styled(Link)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  color: theme.palette.text.light,
  alignItems: 'center',
  textDecoration: 'none',
}));

const ButtonGroupStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(1),
  margin: theme.spacing(0.75, 0),
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1),
    width: '100%',
    '& > *': {
      flex: 1,
      minWidth: 0,
    },
  },
}));

const expressInterestAndQuickApplyButtonStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius * 0.5,
  height: '32px',
  alignItems: 'center',
});
